import React, { useEffect, useRef, useState } from 'react'
import { useDrop } from 'react-dnd'
import Phaser from 'phaser'
import usePlayStore from '../../stores/playStore'
import FieldScene from '../../scenes/FieldScene'

const Field = ({ debugMode = false }) => {
  const gameRef = useRef(null)
  const phaserGameRef = useRef(null)
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 })
  const [fieldCorners, setFieldCorners] = useState({
    topLeft: { x: 0, y: 0 },
    topRight: { x: 0, y: 0 },
    bottomLeft: { x: 0, y: 0 },
    bottomRight: { x: 0, y: 0 }
  })
  const { teams, props, fieldSettings, updateTeam, addTeam, removeTeam, addProp, updateProp, removeProp, animations, currentTime, setSelectedEntity, clearSelectedEntity, selectedEntity } = usePlayStore()

  const [{ isOver }, drop] = useDrop(() => ({
    accept: ['team', 'prop'],
    drop: (item, monitor) => {
      const offset = monitor.getClientOffset()
      if (!offset || !gameRef.current || !phaserGameRef.current) return
      
      const canvas = phaserGameRef.current.canvas
      const scene = phaserGameRef.current.scene.scenes[0]
      if (!canvas || !scene || !scene.screenToWorld) return
      
      // Get screen coordinates relative to canvas
      const canvasRect = canvas.getBoundingClientRect()
      const screenX = offset.x - canvasRect.left
      const screenY = offset.y - canvasRect.top
      
      // Convert to world coordinates using Phaser
      const worldCoords = scene.screenToWorld(screenX, screenY)
      
      if (monitor.getItemType() === 'team') {
        addTeam({
          type: item.type,
          x: worldCoords.x,
          y: worldCoords.y,
          color: item.color,
          number: item.number
        })
      } else if (monitor.getItemType() === 'prop') {
        addProp({
          type: item.type,
          x: worldCoords.x,
          y: worldCoords.y,
          color: item.color
        })
      }
    },
    collect: (monitor) => ({
isOver: monitor.isOver(),
    }),
  }), [addTeam, addProp])

  useEffect(() => {
    if (!gameRef.current) return

    const config = {
      type: Phaser.AUTO,
      width: gameRef.current.offsetWidth,
      height: gameRef.current.offsetHeight,
      parent: gameRef.current,
      backgroundColor: '#22C55E', // Green field color
      physics: {
        default: 'arcade',
        arcade: {
          gravity: { y: 0 },
          debug: false
        }
      },
      scene: [FieldScene]
    }

    phaserGameRef.current = new Phaser.Game(config)

    // Set up the callbacks for team updates when scene is ready
    const setupSceneCallbacks = () => {
      if (phaserGameRef.current && phaserGameRef.current.scene.scenes[0]) {
        const scene = phaserGameRef.current.scene.scenes[0]
        
        // Listen for scene ready event
        scene.events.on('sceneReady', () => {
          // Set up callbacks first
          scene.setTeamPositionUpdateCallback((teamId, x, y) => {
            updateTeam(teamId, { x, y })
          })
          scene.setTeamRemoveCallback((teamId) => {
            removeTeam(teamId)
          })
          scene.setPropPositionUpdateCallback((propId, x, y) => {
            updateProp(propId, { x, y })
          })
          scene.setPropRemoveCallback((propId) => {
            removeProp(propId)
          })
          scene.setEntitySelectCallback((entity) => {
            setSelectedEntity(entity)
          })
          scene.setCurrentSelectionGetter(() => {
            return usePlayStore.getState().selectedEntity
          })
          
          // Make scene accessible globally for animation recording
          window.phaserScene = scene
        })
        
        // If scene is already ready, set up callbacks immediately
        if (scene.scene.isActive()) {
          scene.events.emit('sceneReady')
        }
      } else {
        // Retry after a short delay
        setTimeout(setupSceneCallbacks, 50)
      }
    }

    // Start the setup process
    setupSceneCallbacks()

    return () => {
      if (phaserGameRef.current) {
        phaserGameRef.current.destroy(true)
        phaserGameRef.current = null
      }
      // Clean up global scene reference
      window.phaserScene = null
    }
  }, [updateTeam, removeTeam, updateProp, removeProp])

  // Update field settings when they change
  useEffect(() => {
    if (phaserGameRef.current && phaserGameRef.current.scene.scenes[0]) {
      const scene = phaserGameRef.current.scene.scenes[0]
      scene.updateFieldSettings(fieldSettings)
    }
  }, [fieldSettings])

  // Update teams when they change (count or content changes)
  useEffect(() => {
    if (phaserGameRef.current && phaserGameRef.current.scene.scenes[0]) {
      const scene = phaserGameRef.current.scene.scenes[0]
      // Update teams if count changed or if team IDs changed (for loading different plays)
      const currentTeamIds = teams.map(t => t.id).sort().join(',')
      const lastTeamIds = scene.lastTeamIds || ''
      
      if (scene.lastTeamCount !== teams.length || currentTeamIds !== lastTeamIds) {
        scene.updateTeams(teams)
        scene.lastTeamCount = teams.length
        scene.lastTeamIds = currentTeamIds
      }
    }
  }, [teams]) // Depend on the entire teams array to catch content changes

  // Update props when they change (count or content changes)
  useEffect(() => {
    if (phaserGameRef.current && phaserGameRef.current.scene.scenes[0]) {
      const scene = phaserGameRef.current.scene.scenes[0]
      // Update props if count changed or if prop IDs changed (for loading different plays)
      const currentPropIds = props.map(p => p.id).sort().join(',')
      const lastPropIds = scene.lastPropIds || ''
      
      if (scene.lastPropCount !== props.length || currentPropIds !== lastPropIds) {
        scene.updateProps(props)
        scene.lastPropCount = props.length
        scene.lastPropIds = currentPropIds
      }
    }
  }, [props]) // Depend on the entire props array to catch content changes

  // Update animations when they change
  useEffect(() => {
    if (phaserGameRef.current && phaserGameRef.current.scene.scenes[0]) {
      const scene = phaserGameRef.current.scene.scenes[0]
      scene.updateAnimations(animations, currentTime)
    }
  }, [animations, currentTime])

  // Update selection visuals when selectedEntity changes
  useEffect(() => {
    if (phaserGameRef.current && phaserGameRef.current.scene.scenes[0]) {
      const scene = phaserGameRef.current.scene.scenes[0]
      if (scene.updateSelectionVisuals) {
        // Small delay to ensure sprites are fully created before updating visuals
        setTimeout(() => {
          scene.updateSelectionVisuals(selectedEntity)
        }, 10)
      }
    }
  }, [selectedEntity])

  // Debug mode: Track mouse position and get field corners
  useEffect(() => {
    if (!debugMode || !phaserGameRef.current) return

    let cleanupFunctions = []
    let updateInterval = null

    const canvas = phaserGameRef.current?.canvas
    
    const setupDebugMode = () => {
      const scene = phaserGameRef.current?.scene?.scenes?.[0]
      if (!scene) {
        // Scene not ready yet, retry
        setTimeout(setupDebugMode, 100)
        return
      }

      const updateFieldCorners = () => {
        const bounds = scene.fieldBounds || scene.getFieldBounds?.()
        if (!bounds) {
          return
        }

        setFieldCorners({
          topLeft: { x: Math.round(bounds.x), y: Math.round(bounds.y) },
          topRight: { x: Math.round(bounds.x + bounds.width), y: Math.round(bounds.y) },
          bottomLeft: { x: Math.round(bounds.x), y: Math.round(bounds.y + bounds.height) },
          bottomRight: { x: Math.round(bounds.x + bounds.width), y: Math.round(bounds.y + bounds.height) }
        })
      }

      // Use Phaser's pointer events for more reliable mouse tracking
      const handlePointerMove = (pointer) => {
        if (!scene || !scene.input) return

        // Phaser pointer has worldX and worldY properties that are already in world coordinates
        if (pointer.worldX !== undefined && pointer.worldY !== undefined) {
          setMousePosition({ 
            x: Math.round(pointer.worldX), 
            y: Math.round(pointer.worldY) 
          })
        } else if (scene.screenToWorld) {
          // Fallback: convert screen coordinates to world coordinates
          const worldCoords = scene.screenToWorld(pointer.x, pointer.y)
          setMousePosition({ 
            x: Math.round(worldCoords.x), 
            y: Math.round(worldCoords.y) 
          })
        }
      }

      // Also set up DOM event as fallback
      const handleMouseMove = (event) => {
        if (!canvas || !scene || !scene.screenToWorld) return

        const canvasRect = canvas.getBoundingClientRect()
        const screenX = event.clientX - canvasRect.left
        const screenY = event.clientY - canvasRect.top

        // Convert to world coordinates
        const worldCoords = scene.screenToWorld(screenX, screenY)
        setMousePosition({ x: Math.round(worldCoords.x), y: Math.round(worldCoords.y) })
      }

      const setupEventListeners = () => {
        updateFieldCorners()
        
        // Set up Phaser pointer move listener
        if (scene.input) {
          scene.input.on('pointermove', handlePointerMove)
          cleanupFunctions.push(() => {
            if (scene.input) {
              scene.input.off('pointermove', handlePointerMove)
            }
          })
        }

        // Also set up DOM event as fallback
        if (canvas) {
          canvas.addEventListener('mousemove', handleMouseMove)
          cleanupFunctions.push(() => {
            canvas.removeEventListener('mousemove', handleMouseMove)
          })
        }

        // Update corners periodically
        updateInterval = setInterval(() => {
          updateFieldCorners()
        }, 500)
      }

      // Wait for scene to be fully ready
      if (scene.scene.isActive() && scene.fieldBounds) {
        // Scene is active and has bounds, set up immediately
        setupEventListeners()
      } else {
        // Wait for scene to be ready
        const onSceneReady = () => {
          setupEventListeners()
        }

        scene.events.once('sceneReady', onSceneReady)
        cleanupFunctions.push(() => {
          scene.events.off('sceneReady', onSceneReady)
        })

        // If scene already has bounds, trigger immediately
        if (scene.fieldBounds) {
          onSceneReady()
        }
      }
    }

    setupDebugMode()

    return () => {
      cleanupFunctions.forEach(cleanup => cleanup())
      if (updateInterval) {
        clearInterval(updateInterval)
      }
    }
  }, [debugMode, fieldSettings])

  return (
    <div className="field-container relative">
      <div 
        ref={(node) => {
          gameRef.current = node
          drop(node)
        }}
        className="w-full h-full"
      />
      {debugMode && (
        <div className="absolute top-4 left-4 bg-black bg-opacity-75 text-white p-4 rounded-lg font-mono text-sm z-50 pointer-events-none">
          <div className="font-bold mb-2 text-yellow-400">DEBUG MODE</div>
          <div className="mb-3">
            <div className="font-semibold mb-1">Field Corners:</div>
            <div className="pl-2 space-y-1 text-xs">
              <div>Top-Left: ({fieldCorners.topLeft.x}, {fieldCorners.topLeft.y})</div>
              <div>Top-Right: ({fieldCorners.topRight.x}, {fieldCorners.topRight.y})</div>
              <div>Bottom-Left: ({fieldCorners.bottomLeft.x}, {fieldCorners.bottomLeft.y})</div>
              <div>Bottom-Right: ({fieldCorners.bottomRight.x}, {fieldCorners.bottomRight.y})</div>
            </div>
          </div>
          <div>
            <div className="font-semibold mb-1">Mouse Position:</div>
            <div className="pl-2 text-xs">
              ({mousePosition.x}, {mousePosition.y})
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default Field

